import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE } from "@config/env";
import axios from "axios";
import SectionCard from "./SectionCard";
import { Modal, ModalFooter } from "@common/Modal";

const post = (ep, f) => {
  const fd = new FormData();
  Object.entries(f).forEach(([k,v]) => { if(v!=null) fd.append(k,v); });
  return axios.post(`${API_BASE}${ep}`, fd).then(r=>r.data).catch(()=>null);
};

const STATUS_STYLE = {
  "1": { bg:"var(--fc-primary-light)", color:"var(--fc-primary)", border:"var(--fc-primary-border)" },
  "2": { bg:"var(--fc-amber-light)",   color:"#D97706", border:"var(--fc-amber-border)" },
  "3": { bg:"var(--fc-green-light)",   color:"var(--fc-green)", border:"var(--fc-green-border)" },
  "4": { bg:"var(--fc-red-light)",     color:"#DC2626", border:"var(--fc-red-border)" },
};

export default function ProposeForTab({ resourceSlug, userSlug }) {
  const navigate = useNavigate();

  const [proposed,      setProposed]      = useState([]);
  const [available,     setAvailable]     = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [loadingAvail,  setLoadingAvail]  = useState(false);
  const [showModal,     setShowModal]     = useState(false);
  const [proposing,     setProposing]     = useState({});
  const [done,          setDone]          = useState({});

  // Load already-proposed jobs
  const loadProposed = () => {
    setLoading(true);
    post("get-propose-for", { r_slug: resourceSlug })
      .then(d => {
        setProposed(Array.isArray(d?.job_list) ? d.job_list : []);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (resourceSlug) loadProposed();
  }, [resourceSlug]);

  // Open modal + load available jobs
  const handleOpenModal = () => {
    setShowModal(true);
    setAvailable([]);
    setLoadingAvail(true);
    post("get-available-propose-job", { r_slug: resourceSlug, u_slug: userSlug })
      .then(d => {
        setAvailable(Array.isArray(d?.job_list) ? d.job_list : []);
        setLoadingAvail(false);
      });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setAvailable([]);
  };

  // Propose a candidate for a job
  const propose = async (job) => {
    const key = String(job.job_id);
    setProposing(p => ({ ...p, [key]: true }));
    const res = await post("add-propose-job", {
      u_slug:  userSlug,
      r_slug:  resourceSlug,
      job_id:  job.job_id,
    });
    if (res && Number(res.status) === 1) {
      setDone(d => ({ ...d, [key]: true }));
      loadProposed(); // refresh proposed list
    }
    setProposing(p => ({ ...p, [key]: false }));
  };

  return (
    <>
      <SectionCard title="Opportunities">
        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
          <span style={{ fontSize:13, color:"var(--fc-text-muted)" }}>
            {loading ? "Loading..." : `${proposed.length} job${proposed.length!==1?"s":""} proposed`}
          </span>
          <button
            type="button"
            onClick={handleOpenModal}
            className="fc-btn fc-btn-sm fc-btn-primary fc-btn-pill">
            <i className="fa-solid fa-plus me-1" style={{fontSize:10}} /> Active Jobs
          </button>
        </div>

        {/* Proposed jobs list */}
        {loading ? (
          <div style={{textAlign:"center",color:"var(--fc-text-subtle)",padding:"24px 0"}}>
            <i className="fa-solid fa-spinner fa-spin"/>
          </div>
        ) : proposed.length === 0 ? (
          <div style={{color:"var(--fc-text-subtle)",fontSize:13,textAlign:"center",padding:"24px 0"}}>
            No jobs proposed to this candidate yet.
          </div>
        ) : proposed.map((j, i) => {
          const st = STATUS_STYLE[j.candidate_status] || STATUS_STYLE["2"];
          return (
            <div key={j.auto_id||i} style={{ padding:"12px 16px", background:"var(--fc-bg-light)", border:"1px solid var(--fc-border)", borderRadius:12, marginBottom:8, display:"flex", alignItems:"center", gap:12 }}>
              <div style={{ flex:1, minWidth:0 }}>
                <div onClick={() => navigate(`/job/details/${j.job_slug}`)}
                  style={{ fontSize:13, fontWeight:700, color:"var(--fc-text-primary)", cursor:"pointer" }}>
                  #{j.job_id} {j.job_title}
                </div>
                {j.added_on && (
                  <div style={{ fontSize:11, color:"var(--fc-text-subtle)", marginTop:2 }}>Proposed on {j.added_on}</div>
                )}
              </div>
              <span style={{ fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:99, background:st.bg, color:st.color, border:`1px solid ${st.border}`, flexShrink:0, whiteSpace:"nowrap" }}>
                {j.candidate_propose_status || "Pending"}
              </span>
            </div>
          );
        })}
      </SectionCard>

      {/* Active Jobs modal — rendered OUTSIDE SectionCard to avoid stacking context issues */}
      {showModal && (
        <Modal title="Propose for a Job" onClose={handleCloseModal} maxWidth={480}
          footer={<ModalFooter onConfirm={handleCloseModal} confirmLabel="Close" />}>
          {loadingAvail ? (
            <div style={{textAlign:"center",color:"var(--fc-text-subtle)",padding:"24px 0"}}>
              <i className="fa-solid fa-spinner fa-spin" style={{fontSize:18}}/>
              <div style={{fontSize:13,marginTop:8}}>Loading jobs...</div>
            </div>
          ) : available.length === 0 ? (
            <div style={{textAlign:"center",color:"var(--fc-text-subtle)",padding:"24px 0",fontSize:13}}>
              No available jobs to propose for.
            </div>
          ) : available.map((j, i) => {
            const key = String(j.job_id);
            return (
              <div key={j.job_id||i} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 0", borderBottom:i<available.length-1?"1px solid var(--fc-border-light)":"none" }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:600, color:"var(--fc-text-primary)" }}>#{j.job_id} {j.job_title}</div>
                  {j.employer_name && <div style={{ fontSize:11, color:"var(--fc-text-subtle)", marginTop:2 }}>{j.employer_name}</div>}
                </div>
                <button type="button" onClick={() => propose(j)} disabled={proposing[key] || done[key]}
                  style={{ padding:"6px 14px", borderRadius:8, border:"none", background:done[key]?"var(--fc-green-light)":"var(--fc-primary-light)", color:done[key]?"var(--fc-green)":"var(--fc-primary)", fontSize:12, fontWeight:700, cursor:done[key]||proposing[key]?"default":"pointer", fontFamily:"inherit", flexShrink:0, minWidth:80 }}>
                  {done[key] ? "Proposed ✓" : proposing[key] ? "..." : "Propose"}
                </button>
              </div>
            );
          })}
        </Modal>
      )}
    </>
  );
}
